import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  ConfirmationDialogType,
  Feature,
  InsertSellerEventConfirmationDialogSuppressionRequest,
  SellerUserClient,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export const useEventConfirmationDialogSuppressions = (
  viagogoVirtualId: string | null
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const queryClient = useQueryClient();

  const sellerUserClient = useMemo(
    () => new SellerUserClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  const hasSellerEventAlertMutes = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );

  const shouldQuery =
    activeAccountWebClientConfig.activeAccountId != null &&
    hasSellerEventAlertMutes &&
    !!viagogoVirtualId;

  const queryKey = ['eventConfirmationDialogSuppressions', viagogoVirtualId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return [];
      }

      try {
        return sellerUserClient.getSellerEventConfirmationDialogSuppressions(
          viagogoVirtualId
        );
      } catch (error) {
        trackError(
          'SellerUserClient.getSellerEventConfirmationDialogSuppressions',
          error as ErrorTypes
        );

        return [];
      }
    },
    enabled: shouldQuery,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const mutation = useMutation({
    mutationFn: async (
      request: InsertSellerEventConfirmationDialogSuppressionRequest
    ) => {
      try {
        const result =
          await sellerUserClient.insertSellerEventConfirmationDialogSuppression(
            request
          );

        if (!result.succeeded) {
          throw new Error(result.errorMessage || 'Failed to update alert mute');
        }
        return result;
      } catch (error) {
        trackError(
          'SellerUserClient.insertSellerEventConfirmationDialogSuppression',
          error as ErrorTypes
        );
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey,
      });
    },
  });

  return {
    ...query,
    insertEventConfirmationDialogSuppression: mutation.mutate,
    insertEventConfirmationDialogSuppressionAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
};

export const alertSuppressionKeyToType: Record<
  string,
  ConfirmationDialogType | undefined
> = {
  manualPriceAdjustFloorOrCeilingTimestamp:
    ConfirmationDialogType.FloorCeilingViolation,
  suspiciousPriceSuppressionTimestamp:
    ConfirmationDialogType.SuspiciousPriceChange,
  manualPriceDisableAutoPricingTimestamp:
    ConfirmationDialogType.AutoPricingDisable,
  absolutePriceDropSuppressionTimestamp:
    ConfirmationDialogType.AbsolutePriceDrop,
  relativePriceDropSuppressionTimestamp:
    ConfirmationDialogType.RelativePriceDrop,
  relativeFloorPriceDropSuppressionTimestamp:
    ConfirmationDialogType.RelativeFloorPriceDrop,
  priceStaggerFloorSuppressionTimestamp:
    ConfirmationDialogType.PriceStaggerFloorPriceDrop,
};

export const getConfirmationDialogTypeFromKey = (
  key: string
): ConfirmationDialogType | undefined => {
  return alertSuppressionKeyToType[key];
};
